import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/race-tracker/components/ui/badge";
import { Button } from "@/race-tracker/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/race-tracker/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/race-tracker/components/ui/dialog";
import { Input } from "@/race-tracker/components/ui/input";
import { Label } from "@/race-tracker/components/ui/label";
import { ExternalLink, KeyRound } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../lib/AuthContext";
import { invokeEdgeFunction } from "../../lib/supabaseEdge";

const REFERENCE = [
  { label: "Anthropic", link: "https://console.anthropic.com", text: "console.anthropic.com" },
  { label: "OpenAI", link: "https://platform.openai.com/api-keys", text: "platform.openai.com" },
  { label: "Google AI Studio (Gemini)", link: "https://aistudio.google.com/apikey", text: "aistudio.google.com" },
  { label: "OpenFEC API signup", link: "https://api.open.fec.gov/developers/", text: "api.open.fec.gov" },
];

export default function RaceTrackerSettingsPage() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const isSuperAdmin = profile?.role === "super_admin";

  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [purging, setPurging] = useState(false);
  const [purgeError, setPurgeError] = useState("");
  const [purgeDone, setPurgeDone] = useState(false);

  const runPurge = async () => {
    setPurgeError("");
    setPurging(true);
    try {
      const res = await invokeEdgeFunction("race-tracker-purge-data", {
        method: "POST",
        body: JSON.stringify({ confirm: "DELETE" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || res.statusText || "Purge failed");
      setPurgeDone(true);
      await queryClient.invalidateQueries({ queryKey: ["advertisers"] });
      await queryClient.invalidateQueries({ queryKey: ["races"] });
      await queryClient.invalidateQueries({ queryKey: ["opponents"] });
      await queryClient.invalidateQueries({ queryKey: ["sweepResults"] });
      setPurgeOpen(false);
      setPurgeConfirm("");
    } catch (e) {
      setPurgeError(e.message || "Request failed");
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Settings</h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          Race Tracker configuration, reference links, and data administration.
        </p>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex gap-3 items-start dark:border-amber-900/50 dark:bg-amber-950/30">
        <KeyRound className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-900 dark:text-amber-100 space-y-2">
          <p className="font-semibold">Integration keys</p>
          <p>
            Configure LLM and search API keys in{" "}
            <Link to="/admin/integrations" className="font-semibold text-primary underline">
              Integrations
            </Link>
            . Super Admins can edit keys; Admins can open Integrations in read-only mode. Saved keys display as{" "}
            <strong>***</strong> and are applied server-side for sweeps.
          </p>
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold mb-2">Reference links</h2>
        <ul className="space-y-2">
          {REFERENCE.map((r) => (
            <li key={r.label} className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
              <Badge variant="outline" className="text-[10px] shrink-0">
                {r.label}
              </Badge>
              <a
                href={r.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-0.5"
              >
                {r.text} <ExternalLink className="w-2.5 h-2.5" />
              </a>
            </li>
          ))}
        </ul>
      </div>

      {isSuperAdmin ? (
        <Card className="border-destructive/40 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-destructive">Danger Zone</CardTitle>
            <CardDescription className="text-xs leading-relaxed">
              Permanently deletes all advertisers, races, opponents, and sweep results. Use this before a fresh bulk import. This cannot be undone.
              Saved integration keys (<code className="text-[10px] bg-muted px-1 rounded">rt_integration_settings</code>) are not removed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button type="button" variant="destructive" size="sm" className="text-xs" onClick={() => {
              setPurgeError("");
              setPurgeDone(false);
              setPurgeConfirm("");
              setPurgeOpen(true);
            }}>
              Delete All Data…
            </Button>
            {purgeDone && (
              <p className="text-xs text-emerald-600 mt-3 mb-0">All Race Tracker data was deleted.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <p className="text-xs text-muted-foreground border rounded-lg px-3 py-2 bg-muted/40">
          Danger Zone actions require a Super Admin role.
        </p>
      )}

      <Dialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete all Race Tracker data?</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              This permanently deletes every advertiser, race, opponent, and sweep result. Type <strong>DELETE</strong> to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="purge-confirm" className="text-xs">
              Type DELETE to confirm
            </Label>
            <Input
              id="purge-confirm"
              autoComplete="off"
              value={purgeConfirm}
              onChange={(e) => setPurgeConfirm(e.target.value)}
              placeholder="DELETE"
              className="font-mono text-sm"
            />
            {purgeError ? <p className="text-xs text-destructive m-0">{purgeError}</p> : null}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" size="sm" onClick={() => setPurgeOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={purgeConfirm.trim() !== "DELETE" || purging}
              onClick={runPurge}
            >
              {purging ? "Deleting…" : "Delete All"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
